import React, { useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";

/**
 * Preview-only: soft fill tied to the camera so FP / wide-FOV preview reads closer to the
 * editor orbit view, without changing shared presence materials (no extra metalness, etc.).
 */
export default function PreviewPresenceLighting({
  intensity = 0.42,
  color = "#e8eaef",
  transitionSpeed = 6,
}) {
  const lightRef = useRef(null);
  const fwd = useRef(new THREE.Vector3());
  const targetIntensity = useRef(intensity);
  const targetColor = useRef(new THREE.Color(color));

  targetIntensity.current = intensity;
  targetColor.current.set(color);

  useFrame((state, delta) => {
    const { camera } = state;
    const L = lightRef.current;
    if (!L) return;
    const alpha = 1 - Math.exp(-transitionSpeed * delta);
    const prevIntensity = L.intensity;
    const prevColor = L.color.clone();
    L.position.copy(camera.position);
    camera.getWorldDirection(fwd.current);
    L.target.position.copy(camera.position).add(fwd.current.multiplyScalar(5));
    L.target.updateMatrixWorld();
    L.intensity += (targetIntensity.current - L.intensity) * alpha;
    L.color.lerp(targetColor.current, alpha);
    const moved =
      Math.abs(L.intensity - prevIntensity) +
      Math.abs(L.color.r - prevColor.r) +
      Math.abs(L.color.g - prevColor.g) +
      Math.abs(L.color.b - prevColor.b);
    if (moved > 0.0002) state.invalidate();
  });

  return (
    <directionalLight
      ref={lightRef}
      intensity={intensity}
      color={color}
      castShadow={false}
    />
  );
}
